// FILE: src/services/paceService.js
//
// Intelligent Pace Coaching — the Pace Engine.
//
// A PURE, side-effect-free module. It computes pacing state from primitives and
// returns a snapshot. It holds no React state, touches no DOM, performs no I/O,
// and reads no config from `exams.js` directly — the caller passes thresholds in
// (defaulting to DEFAULT_PACE_CONFIG). Every UI surface (the live Pace card, the
// intervention controller, the break panel, the results analytics) is a view of
// this module's output.
//
// Design reference: docs/pace-coaching-proposal.md (Revision 2). Phase 1 ships
// this engine + its unit tests only — no UI, no persistence, no exams.js wiring.
//
// Two signals come out of computePace():
//   • confidence — the PRIMARY, learner-facing classification, derived from the
//     projected REVIEW-TIME buffer (what the learner actually cares about).
//   • status     — a coarse pace band used ONLY to decide when an intervention
//     message fires.
// They are computed from one snapshot and always stay consistent.

/**
 * Default, per-exam-overridable pace configuration. Mirrors the `paceCoaching`
 * block documented in docs/pace-coaching-proposal.md §8. In phase 2 these are
 * sourced from exams.js per cert; the engine itself only needs the shape.
 */
export const DEFAULT_PACE_CONFIG = Object.freeze({
  // Confidence indicator — PRIMARY (review-time forecast). Minutes of projected
  // review buffer at/above which each label applies. Checked high → low.
  confidenceMinutes: { comfortable: 20, good: 10, tight: 5, atRisk: 0 },

  // Pace status bands — for intervention messaging only. Seconds BEHIND target.
  statusThresholdsSeconds: { ahead: 60, behind: 180, significantlyBehind: 600 },

  // Exam-aware milestone positions (answered-question counts), NOT an interval.
  milestones: [60, 120, 150],

  timeSinkSeconds: 180,          // single-question "time sink" nudge threshold
  recoveryMarginSeconds: 120,    // upward swing that qualifies as a "recovery"
  warmupQuestions: 20,           // below this, observed rate is too noisy to judge
});

/** Clamp n into [lo, hi]. */
function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

/** True when v is a finite, usable number. */
function isNum(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Classify the PRIMARY confidence label from projected review-time buffer.
 * @param {number} reviewBufferSeconds  duration - projectedFinish (may be < 0)
 * @param {object} [bands]              minutes thresholds (see DEFAULT_PACE_CONFIG)
 * @returns {'comfortable'|'good'|'tight'|'atRisk'|'unlikely'}
 */
export function classifyConfidence(reviewBufferSeconds, bands = DEFAULT_PACE_CONFIG.confidenceMinutes) {
  const mins = (isNum(reviewBufferSeconds) ? reviewBufferSeconds : 0) / 60;
  if (mins >= bands.comfortable) return 'comfortable';
  if (mins >= bands.good) return 'good';
  if (mins >= bands.tight) return 'tight';
  if (mins >= bands.atRisk) return 'atRisk';
  return 'unlikely';
}

/**
 * Classify the coarse pace status band used to gate intervention messages.
 * Positive deltaSeconds = ahead of pace; negative = behind.
 * @param {number} deltaSeconds
 * @param {object} [bands]  seconds thresholds (see DEFAULT_PACE_CONFIG)
 * @returns {'ahead'|'onTrack'|'behind'|'significantlyBehind'}
 */
export function classifyStatus(deltaSeconds, bands = DEFAULT_PACE_CONFIG.statusThresholdsSeconds) {
  const d = isNum(deltaSeconds) ? deltaSeconds : 0;
  if (d >= bands.ahead) return 'ahead';
  const behind = -d; // seconds behind target
  if (behind >= bands.significantlyBehind) return 'significantlyBehind';
  if (behind >= bands.behind) return 'behind';
  return 'onTrack';
}

/**
 * Map a snapshot to a break-coaching recommendation bucket, by confidence.
 * @param {object} snapshot  output of computePace()
 * @returns {'onTrack'|'behind'|'wellBehind'}
 */
export function paceRecommendationKey(snapshot) {
  if (!snapshot || !snapshot.valid) return 'onTrack';
  if (snapshot.confidence === 'comfortable' || snapshot.confidence === 'good') return 'onTrack';
  if (snapshot.confidence === 'unlikely') return 'wellBehind';
  return 'behind'; // tight | atRisk
}

/**
 * Core engine. Compute a pacing snapshot from session primitives.
 *
 * @param {object} p
 * @param {number} p.totalQuestions   session.questions.length
 * @param {number} p.durationSeconds  session.durationMinutes * 60
 * @param {number} p.answeredCount    # questions with submitted === true
 * @param {number} p.elapsedSeconds   authoritative elapsed (timer-derived)
 * @param {object} [p.config]         pace config (defaults to DEFAULT_PACE_CONFIG)
 * @returns {object} snapshot
 */
/**
 * Single source of truth for whether pace coaching should be active for a
 * session. Used by BOTH the live in-exam gate (App.js) and the results-page
 * analytics gate (ResultsPage) so the two can never drift apart again.
 *
 * @param {object}  p
 * @param {object}  p.paceConfig     resolved getPaceConfig(examId) (or null)
 * @param {string}  p.mode           session mode ('full'|'practice'|'targetedTest'|...)
 * @param {boolean} p.prefEnabled    learner master "Pace coaching" toggle
 * @param {boolean} p.targetedOptIn  learner "Pace in targeted tests" opt-in
 * @returns {boolean}
 */
export function isPaceEnabled({ paceConfig, mode, prefEnabled, targetedOptIn } = {}) {
  if (!paceConfig || !paceConfig.enabled) return false;
  if (!Array.isArray(paceConfig.appliesTo) || !paceConfig.appliesTo.includes(mode)) return false;
  if (!prefEnabled) return false;                       // master toggle off
  if (mode === 'targetedTest' && !targetedOptIn) return false; // targeted opt-in
  return true;
}

export function computePace({ totalQuestions, durationSeconds, answeredCount, elapsedSeconds, config } = {}) {
  const cfg = { ...DEFAULT_PACE_CONFIG, ...(config || {}) };

  // Defensive guard: invalid session shape yields a safe, neutral snapshot
  // rather than NaNs leaking into the UI.
  if (!isNum(totalQuestions) || totalQuestions <= 0 || !isNum(durationSeconds) || durationSeconds <= 0) {
    return {
      // Invalid session: confidence is null (UNKNOWN), never a positive label.
      // The UI must gate display on `valid` and treat null confidence as "no
      // pace info to show" rather than rendering a colored chip.
      valid: false, warmup: true,
      reviewBufferSeconds: 0, projectedFinishSeconds: 0, projectedUnanswered: 0, confidence: null,
      deltaSeconds: 0, deltaQuestions: 0, status: 'onTrack',
      expectedAnswered: 0, secPerQ: 0, observedSecPerQ: 0, percentComplete: 0,
      answeredCount: 0, totalQuestions: 0,
    };
  }

  const answered = clamp(isNum(answeredCount) ? answeredCount : 0, 0, totalQuestions);
  const elapsed = Math.max(0, isNum(elapsedSeconds) ? elapsedSeconds : 0);

  const secPerQ = durationSeconds / totalQuestions;             // target rate
  const expectedAnswered = clamp(elapsed / secPerQ, 0, totalQuestions);
  const deltaQuestions = answered - expectedAnswered;           // +ahead / -behind
  const deltaSeconds = deltaQuestions * secPerQ;

  const remainingQ = totalQuestions - answered;
  // Before warmup we have no reliable observed rate, so fall back to the target
  // rate (the neutral assumption) instead of dividing by a tiny answered count.
  //
  // Adaptive warmup: a short session (e.g. a 20-question practice exam) can't
  // wait the full configured warmup before pacing means anything. Scale the
  // warmup down to ~1/4 of the session, floored at 3 (too few to judge a rate)
  // and capped at the configured value, so full-length exams keep their
  // original warmup while short sessions still get live pace.
  const configuredWarmup = isNum(cfg.warmupQuestions) ? cfg.warmupQuestions : 0;
  const effectiveWarmup = Math.max(3, Math.min(configuredWarmup, Math.ceil(totalQuestions * 0.25)));
  const warmup = answered < effectiveWarmup;
  const observedSecPerQ = answered > 0 && !warmup ? elapsed / answered : secPerQ;

  const projectedFinishSeconds = elapsed + remainingQ * observedSecPerQ;
  const reviewBufferSeconds = durationSeconds - projectedFinishSeconds;

  // Projected questions left UNANSWERED if the current pace holds until time
  // runs out. The complement of the review buffer: one is positive only when
  // the other is zero. Powers the live card's "behind" framing.
  const remainingTimeSeconds = Math.max(0, durationSeconds - elapsed);
  const answerableRemaining = observedSecPerQ > 0 ? remainingTimeSeconds / observedSecPerQ : remainingQ;
  const projectedUnanswered = clamp(totalQuestions - (answered + answerableRemaining), 0, remainingQ);

  return {
    valid: true,
    warmup,
    // PRIMARY
    reviewBufferSeconds,
    projectedFinishSeconds,
    projectedUnanswered,
    confidence: classifyConfidence(reviewBufferSeconds, cfg.confidenceMinutes),
    // SECONDARY (de-emphasized in UI)
    deltaSeconds,
    deltaQuestions,
    status: classifyStatus(deltaSeconds, cfg.statusThresholdsSeconds),
    // SUPPORTING
    expectedAnswered,
    secPerQ,
    observedSecPerQ,
    percentComplete: answered / totalQuestions,
    answeredCount: answered,
    totalQuestions,
  };
}

/** True when the current question has crossed the time-sink threshold. */
export function shouldShowTimeSink(currentQuestionSeconds, config = DEFAULT_PACE_CONFIG) {
  const cfg = { ...DEFAULT_PACE_CONFIG, ...(config || {}) };
  return isNum(currentQuestionSeconds) && currentQuestionSeconds >= cfg.timeSinkSeconds;
}

/** True when answeredCount lands exactly on a configured milestone. */
export function isMilestone(answeredCount, config = DEFAULT_PACE_CONFIG) {
  const cfg = { ...DEFAULT_PACE_CONFIG, ...(config || {}) };
  return Array.isArray(cfg.milestones) && cfg.milestones.includes(answeredCount);
}

/**
 * Detect a "pace recovery": the learner was behind and has climbed back up by a
 * meaningful margin. Used (sparingly) to fire an encouraging message.
 * @param {number} prevDeltaSeconds  delta at the previous evaluation
 * @param {number} currDeltaSeconds  delta now
 * @param {object} [config]
 * @returns {boolean}
 */
export function detectRecovery(prevDeltaSeconds, currDeltaSeconds, config = DEFAULT_PACE_CONFIG) {
  const cfg = { ...DEFAULT_PACE_CONFIG, ...(config || {}) };
  if (!isNum(prevDeltaSeconds) || !isNum(currDeltaSeconds)) return false;
  const wasBehind = prevDeltaSeconds <= -cfg.statusThresholdsSeconds.behind;
  const improved = currDeltaSeconds - prevDeltaSeconds >= cfg.recoveryMarginSeconds;
  const nowRecovered = currDeltaSeconds > -cfg.statusThresholdsSeconds.behind;
  return wasBehind && improved && nowRecovered;
}

/**
 * Derive advisory personalization tags from aggregate behavior. All inputs are
 * precomputed scalars so the engine stays decoupled from question internals.
 *
 * @param {object} p
 * @param {object} p.snapshot          output of computePace()
 * @param {number} p.answeredCount
 * @param {number} [p.correctCount]    answered-and-correct (computed internally by App.js)
 * @param {number} [p.flaggedCount]
 * @param {number} [p.answerChangeCount]
 * @param {object} [p.config]
 * @returns {{tags: string[], dominant: string|null}}
 */
export function derivePaceProfile({ snapshot, answeredCount, correctCount, flaggedCount, answerChangeCount, config } = {}) {
  const tags = [];
  if (!snapshot || !snapshot.valid || !isNum(answeredCount) || answeredCount <= 0) {
    return { tags, dominant: null };
  }
  const { observedSecPerQ, secPerQ } = snapshot;
  const accuracy = isNum(correctCount) ? correctCount / answeredCount : null;
  const flagRatio = isNum(flaggedCount) ? flaggedCount / answeredCount : 0;
  const changeRatio = isNum(answerChangeCount) ? answerChangeCount / answeredCount : 0;

  if (accuracy !== null && observedSecPerQ > secPerQ * 1.1 && accuracy >= 0.7) {
    tags.push('slowButAccurate');
  }
  if (accuracy !== null && observedSecPerQ < secPerQ * 0.9 && accuracy < 0.6) {
    tags.push('fastButErrorProne');
  }
  if (flagRatio > 0.15) tags.push('highFlagCount');
  if (changeRatio > 0.3) tags.push('frequentAnswerChanges');

  // Priority order: accuracy-quality tags first, then strategy tags.
  const priority = ['fastButErrorProne', 'slowButAccurate', 'frequentAnswerChanges', 'highFlagCount'];
  const dominant = priority.find((t) => tags.includes(t)) || null;
  return { tags, dominant };
}

/**
 * End-of-exam time-management analytics. Pure over the per-question timing that
 * App.js already accumulates (questionStates[i].timeSpentSeconds) and persists.
 *
 * @param {object} session            { durationMinutes, questions }
 * @param {object} questionStates     { [i]: { submitted, timeSpentSeconds } }
 * @param {object} [extra]            { flaggedCount, answerChangeCount }
 * @returns {object} analytics
 */
export function computePaceAnalytics(session, questionStates, extra = {}) {
  const total = session && Array.isArray(session.questions) ? session.questions.length : 0;
  const durationSeconds = session && isNum(session.durationMinutes) ? session.durationMinutes * 60 : 0;
  const states = questionStates || {};

  const times = [];
  let answered = 0;
  let changeSum = 0;
  for (let i = 0; i < total; i += 1) {
    const s = states[i] || {};
    const t = isNum(s.timeSpentSeconds) ? s.timeSpentSeconds : 0;
    if (s.submitted) {
      answered += 1;
      changeSum += isNum(s.answerChanges) ? s.answerChanges : 0;
    }
    if (s.submitted && t > 0) times.push(t);
  }
  const targetSecPerQ = total > 0 ? durationSeconds / total : 0;

  const totalTimeSeconds = times.reduce((a, b) => a + b, 0);
  const count = times.length;
  const avgSecondsPerQuestion = count > 0 ? totalTimeSeconds / count : 0;
  const longestQuestionSeconds = count > 0 ? Math.max(...times) : 0;
  const fastestQuestionSeconds = count > 0 ? Math.min(...times) : 0;

  // Pace consistency: lower spread = steadier. Coefficient of variation (stdev/mean).
  let paceConsistency = 0;
  if (count > 1 && avgSecondsPerQuestion > 0) {
    const variance = times.reduce((a, t) => a + (t - avgSecondsPerQuestion) ** 2, 0) / count;
    paceConsistency = Math.sqrt(variance) / avgSecondsPerQuestion;
  }

  // Recovery: did the second half run faster (per-q) than the first half?
  const half = Math.floor(count / 2);
  const firstAvg = half > 0 ? times.slice(0, half).reduce((a, b) => a + b, 0) / half : 0;
  const secondAvg = count - half > 0 ? times.slice(half).reduce((a, b) => a + b, 0) / (count - half) : 0;
  const recoveredSeconds = firstAvg - secondAvg; // positive = sped up later

  // Prefer the caller's authoritative elapsed time (from the timer at finish)
  // when provided. The summed per-question time UNDERCOUNTS true elapsed — it
  // excludes idle time, time spent on the explanation panel, and any gaps — so
  // relying on it alone makes the completion margin optimistically large. Fall
  // back to the summed time only when no explicit elapsed is supplied.
  const elapsedAtFinish = isNum(extra.elapsedSeconds) ? extra.elapsedSeconds : totalTimeSeconds;
  const completionMarginSeconds = durationSeconds - elapsedAtFinish;

  // Pace trend: average per-question seconds per fifth of the exam (for the
  // lightweight results sparkline). Empty blocks report 0.
  const blocks = 5;
  const trend = [];
  if (count > 0) {
    const per = Math.ceil(count / blocks);
    for (let b = 0; b < blocks; b += 1) {
      const slice = times.slice(b * per, (b + 1) * per);
      trend.push(slice.length ? slice.reduce((a, x) => a + x, 0) / slice.length : 0);
    }
  }

  return {
    answered,
    totalQuestions: total,
    avgSecondsPerQuestion,
    longestQuestionSeconds,
    fastestQuestionSeconds,
    totalTimeSeconds,
    elapsedSeconds: elapsedAtFinish,
    completionMarginSeconds,
    finishedAhead: completionMarginSeconds >= 0,
    paceConsistency,
    recoveredSeconds,
    trend,
    targetSecPerQ,
    flaggedCount: isNum(extra.flaggedCount) ? extra.flaggedCount : 0,
    answerChangeCount: isNum(extra.answerChangeCount) ? extra.answerChangeCount : changeSum,
  };
}

/**
 * A short, plain-language coaching summary of the learner's time management for
 * the results page. Pure: derived entirely from a computePaceAnalytics() object.
 * @param {object} analytics  output of computePaceAnalytics()
 * @returns {string}
 */
export function paceResultsSummary(analytics) {
  if (!analytics || !analytics.answered) return '';
  const marginMin = Math.abs(Math.round((analytics.completionMarginSeconds || 0) / 60));
  const trend = Array.isArray(analytics.trend) ? analytics.trend : [];
  const labels = ['the opening', 'the early-middle', 'the middle', 'the late-middle', 'the final'];
  let slowest = '';
  if (trend.length) {
    let maxIdx = 0;
    trend.forEach((v, i) => { if (v > trend[maxIdx]) maxIdx = i; });
    slowest = labels[Math.min(maxIdx, labels.length - 1)] || '';
  }
  const steady = (analytics.paceConsistency || 0) < 0.35;

  if (analytics.finishedAhead) {
    if (steady) {
      return `Strong, steady time management — you finished with about ${marginMin} min to spare. Keep this rhythm.`;
    }
    return `You finished with about ${marginMin} min to spare, but your pace was uneven${slowest ? `, slowing most during ${slowest}` : ''}. Smoothing that out earns even more review time.`;
  }
  return `Time management is your biggest opportunity — at this pace you'd run about ${marginMin} min over${slowest ? `, spending the most time during ${slowest}` : ''}. Practicing additional timed sessions should improve pacing and preserve review time.`;
}
